import { useEffect, useRef } from 'react'

import { CloseButton, TextInput } from '@mantine/core'

import IconSearch from 'src/icons/IconSearch'
import { cn } from 'src/utils'

export interface SearchBoxProps {
  value: string
  setValue: (value: string) => void
  placeholder?: string
  onSearch: (value: string) => void
  className?: string
  size?: string
  /**
   * Accessible name for the input. A placeholder is not a label — it is not
   * announced once text is typed, and some screen readers skip it entirely.
   */
  ariaLabel?: string
  focusOnMount?: boolean
  onFocus?: () => void
  onBlur?: () => void
  onEscape?: () => void
}
export const SearchBox = ({
  value,
  setValue = () => {},
  placeholder = '',
  onSearch,
  className = 'flex-1',
  size,
  ariaLabel,
  focusOnMount = false,
  onFocus,
  onBlur,
  onEscape,
}: SearchBoxProps) => {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (focusOnMount) inputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch(value)
    }
    if (event.key === 'Escape') {
      onEscape?.()
    }
  }

  const handleClear = () => {
    setValue('')
    onSearch('')
  }

  return (
    <TextInput
      className={cn('font-normal', className)}
      size={size}
      aria-label={ariaLabel}
      leftSection={
        <IconSearch className="h-3 w-3 fill-none stroke-doubleNickel-gray-700" />
      }
      rightSection={
        value ? (
          <CloseButton
            size="sm"
            aria-label="Clear search"
            onClick={handleClear}
          />
        ) : null
      }
      placeholder={placeholder}
      value={value}
      ref={inputRef}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={(event) => setValue(event.currentTarget.value)}
      onKeyDown={handleKeyPress}
    />
  )
}
